import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Box, Button, Text, Select, Group, Stack, ActionIcon, LoadingOverlay, TextInput } from '@mantine/core';
import { IconTrash, IconPlus } from '@tabler/icons-react';
import { PersonBetter, Relationship, RelationshipType, RelationshipTypes, formatRelationshipTypeForDisplay, standardizeRelationshipType } from "../../types/Person";
import { useDebouncedCallback } from 'use-debounce';
import {fetchPeople} from "../../api/People";

interface RelationshipsPanelProps {
    person: PersonBetter;
    onUpdate: (updatedFields: Partial<PersonBetter>) => Promise<void>;
    isUpdating: boolean;
}

export const RelationshipsPanel: React.FC<RelationshipsPanelProps> = React.memo(({
                                                                                     person,
                                                                                     onUpdate,
                                                                                     isUpdating,
                                                                                 }) => {

    const [fullPeople, setFullPeople] = useState<PersonBetter[]>([]);
    const [people, setPeople] = useState<{value: string, label: string}[]>([]);
    const [relationships, setRelationships] = useState<Relationship[]>(() => {
        const initialRelationships = person.relationships || [];
        
        // Map relationships and ensure type is properly formatted
        return initialRelationships.map(rel => {
            // Standardize the relationship type to ensure it's in the correct format
            const standardizedType = rel.type ? standardizeRelationshipType(rel.type.toString()) : null;
            
            // Return the relationship with the correct name field and standardized type
            return {
                ...rel,
                type: standardizedType,
                name: rel.person?.seed_key || null
            };
        });
    });

    const [hasPendingChanges, setHasPendingChanges] = useState(false);
    const lastSavedRef = useRef<Relationship[]>(person.relationships || []);
    const isSavingRef = useRef(false);

    // Helper function to find the matching relationship type in the enum
    const findMatchingRelationshipType = (type: string | null): RelationshipType | undefined => {
        if (!type) return undefined;
        
        // First try direct match
        if (Object.values(RelationshipType).includes(type as any)) {
            return type as RelationshipType;
        }
        
        // Try to match by display value
        const displayValue = formatRelationshipTypeForDisplay(type);
        for (const enumType of RelationshipTypes) {
            if (formatRelationshipTypeForDisplay(enumType) === displayValue) {
                return enumType;
            }
        }
        
        // Try to match by standardized value
        const standardized = standardizeRelationshipType(type);
        if (standardized && Object.values(RelationshipType).includes(standardized)) {
            return standardized;
        }
        
        console.log(`Could not find matching relationship type for: ${type}`);
        return undefined;
    };
    


    useEffect(() => {
        const loadPeople = async () => {
            try {
                const fetchedPeople = await fetchPeople();
                setFullPeople(fetchedPeople);
                let pairs: { value: string; label: string }[] = fetchedPeople.map((p: PersonBetter) => ({
                    value: p.seed_key,
                    label: `${p.first} ${p.last}`.trim(),
                }));
                setPeople(pairs);
            } catch (error) {
                console.error('Failed to load people for autocomplete:', error);
            }
        };
        loadPeople();
    }, []);

    const hasUnsavedChanges = useCallback(() => {
        const currentRelationships = JSON.stringify(relationships);
        const lastSaved = JSON.stringify(lastSavedRef.current);
        return currentRelationships !== lastSaved;
    }, [relationships]);

    const saveChanges = async (updatedRelationships: Relationship[]) => {
        if (isSavingRef.current) {
            return;
        }
        isSavingRef.current = true;
        setHasPendingChanges(false);

        try {
            await onUpdate({ relationships: updatedRelationships });
            lastSavedRef.current = updatedRelationships;
        } catch (error) {
            console.error('Failed to save relationships:', error);
            // Silently fail - keep local changes even if save fails
        } finally {
            isSavingRef.current = false;
        }
    };

    const debouncedSave = useDebouncedCallback(
        (updatedRelationships: Relationship[]) => {
            if (hasUnsavedChanges()) {
                saveChanges(updatedRelationships);
            }
        },
        3000,
        { maxWait: 5000 }
    );

    const handleAddRelationship = useCallback(() => {
        const newRelationships = [...relationships, {
            type: null,
            name: null,
            person: undefined
        }];
        setRelationships(newRelationships);
        setHasPendingChanges(true);
    }, [relationships]);

    const handleRemoveRelationship = useCallback(async (index: number) => {
        const updatedRelationships = relationships.filter((_, i) => i !== index);
        setRelationships(updatedRelationships);
        setHasPendingChanges(true);
        debouncedSave(updatedRelationships);
    }, [relationships, debouncedSave]);

    const handleUpdateRelationship = useCallback((
        index: number,
        field: 'type' | 'name' | 'custom_type',
        value: string | null
    ) => {
        const updatedRelationships = relationships.map((rel, i) => {
            if (i === index) {
                let updatedRel: Relationship;
                
                if (field === 'name') {
                    const selectedPerson: PersonBetter | undefined = fullPeople.find((p:PersonBetter) => p.seed_key === value);
                    updatedRel = {
                        ...rel,
                        name: value,
                        person: selectedPerson || undefined
                    } as Relationship;
                } else if (field === 'custom_type') {
                    updatedRel = {
                        ...rel,
                        custom_type: value,
                        type: RelationshipType.IsOtherOf
                    } as Relationship;
                } else {
                    updatedRel = {
                        ...rel,
                        [field]: field === 'type' ? (value as RelationshipType | null) : value,
                        // Clear custom_type if a standard type is selected
                        ...(field === 'type' && value !== RelationshipType.IsOtherOf ? { custom_type: undefined } : {})
                    } as Relationship;
                }
                
                return updatedRel;
            }
            return rel;
        });

        setRelationships(updatedRelationships);
        setHasPendingChanges(true);
        
        // Only save if there are complete relationships (with both type and person)
        const hasIncompleteRelationships = updatedRelationships.some(rel => {
            // Check if this relationship is incomplete
            const hasType = !!rel.type;
            const hasPerson = !!(rel.person?.seed_key || rel.name);
            return !hasType || !hasPerson;
        });
        
        if (!hasIncompleteRelationships) {
            console.log('All relationships are complete, saving changes');
            debouncedSave(updatedRelationships);
        } else {
            console.log('Some relationships are incomplete, not saving yet');
        }
    }, [relationships, debouncedSave, fullPeople]);

    const handleBlur = useCallback(() => {
        if (hasPendingChanges) {
            // Check if all relationships are complete before saving
            const hasIncompleteRelationships = relationships.some(rel => {
                const hasType = !!rel.type;
                const hasPerson = !!(rel.person?.seed_key || rel.name);
                return !hasType || !hasPerson;
            });
            
            if (!hasIncompleteRelationships) {
                console.log('All relationships are complete on blur, saving changes');
                debouncedSave.flush();
            } else {
                console.log('Some relationships are incomplete on blur, not saving yet');
            }
        }
    }, [hasPendingChanges, debouncedSave, relationships]);

    useEffect(() => {
        if (JSON.stringify(person.relationships) !== JSON.stringify(lastSavedRef.current)) {
            setRelationships(person.relationships || []);
            lastSavedRef.current = person.relationships || [];
        }
    }, [person.relationships]);

    useEffect(() => {
        return () => {
            if (hasPendingChanges && !isSavingRef.current) {
                debouncedSave.flush();
            }
        };
    }, [hasPendingChanges, debouncedSave]);

    return (
        <Box mt={20} p={20} pos="relative">
            <LoadingOverlay visible={isUpdating} />
            <Stack gap="md">
                {relationships.length === 0 ? (
                    <Text>No relationships have been added yet.</Text>
                ) : (
                    relationships.map((relationship, index) => (
                        <Group key={index} justify="space-between" align="center">
                            <Select
                                data={RelationshipTypes.map(type => ({
                                    value: type,
                                    label: formatRelationshipTypeForDisplay(type)
                                }))}
                                value={findMatchingRelationshipType(relationship.type as string) || undefined}
                                onChange={(value) => handleUpdateRelationship(index, 'type', value)}
                                onBlur={handleBlur}
                                placeholder="Select relationship type"
                                style={{ flex: 1 }}
                                disabled={isUpdating}
                                searchable
                                clearable
                            />

                            <Text>of</Text>
                            {relationship.type === RelationshipType.IsOtherOf && (
                                <TextInput
                                    value={relationship.custom_type || ''}
                                    onChange={(e) => handleUpdateRelationship(index, 'custom_type', e.currentTarget.value)}
                                    onBlur={handleBlur}
                                    placeholder="Custom relationship type"
                                    style={{ flex: 1 }}
                                    disabled={isUpdating}
                                />
                            )}
                            <Select
                                data={people}
                                value={relationship.name || ''}
                                onChange={(value) => handleUpdateRelationship(index, 'name', value)}
                                onBlur={handleBlur}
                                placeholder="Select Person"
                                style={{ flex: 1 }}
                                disabled={isUpdating}
                            />
                            <ActionIcon
                                color="red"
                                onClick={() => handleRemoveRelationship(index)}
                                variant="outline"
                                disabled={isUpdating}
                            >
                                <IconTrash size={16} />
                            </ActionIcon>
                        </Group>
                    ))
                )}

                <Button
                    onClick={handleAddRelationship}
                    leftSection={<IconPlus size={16} />}
                    variant="outline"
                    disabled={isUpdating}
                >
                    Add Relationship
                </Button>
            </Stack>
        </Box>
    );
});

RelationshipsPanel.displayName = 'RelationshipsPanel';